import { APP_CONSTANTS } from '@/config/constants';

export type LocalPullProtection = {
  entityKeys: Set<string>;
  checklistValueKeys: Set<string>;
  checklistValueChecklistIds: Set<string>;
};

export const createEmptyLocalPullProtection = (): LocalPullProtection => ({
  entityKeys: new Set<string>(),
  checklistValueKeys: new Set<string>(),
  checklistValueChecklistIds: new Set<string>(),
});

const localPullEntityKey = (collection: string, itemId: string): string => (
  `${collection}:${itemId}`
);

const localChecklistValueKey = (checklistId: string, campoId: string): string => (
  `${checklistId}:${campoId}`
);

export const parsePullProtectionData = (data: unknown): Record<string, unknown> | null => {
  const parsed = typeof data === 'string'
    ? (() => {
        try {
          return JSON.parse(data);
        } catch {
          return null;
        }
      })()
    : data;

  return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? parsed as Record<string, unknown>
    : null;
};

const addPullProtectionEntity = (
  protection: LocalPullProtection,
  collection: unknown,
  itemId: unknown
) => {
  const collectionName = String(collection ?? '').trim();
  const localItemId = String(itemId ?? '').trim();
  if (collectionName && localItemId) {
    protection.entityKeys.add(localPullEntityKey(collectionName, localItemId));
  }
};

const addChecklistValueProtection = (
  protection: LocalPullProtection,
  checklistId: unknown,
  campoId?: unknown
) => {
  const normalizedChecklistId = String(checklistId ?? '').trim();
  const normalizedCampoId = String(campoId ?? '').trim();
  if (!normalizedChecklistId) return;

  if (normalizedCampoId) {
    protection.checklistValueKeys.add(
      localChecklistValueKey(normalizedChecklistId, normalizedCampoId)
    );
    return;
  }

  protection.checklistValueChecklistIds.add(normalizedChecklistId);
};

const addChecklistPartialProtection = (
  protection: LocalPullProtection,
  checklistId: unknown,
  data: unknown
) => {
  const payload = parsePullProtectionData(data);
  const fieldIds = payload
    ? Object.keys(payload).map(fieldId => fieldId.trim()).filter(Boolean)
    : [];

  if (fieldIds.length === 0) {
    addChecklistValueProtection(protection, checklistId);
    return;
  }

  fieldIds.forEach(fieldId => addChecklistValueProtection(protection, checklistId, fieldId));
};

export const buildLocalPullProtection = (snapshot: { operations: Record<string, any>[]; images: Record<string, any>[]; deletions: Record<string, any>[]; conflicts: Record<string, any>[] }, allowMissingParentMetadata = false): LocalPullProtection => {
  const {
    operations: pendingOperations,
    images: pendingImages,
    deletions: pendingDeletions,
    conflicts,
  } = snapshot;
  const protection: LocalPullProtection = {
    entityKeys: new Set<string>(),
    checklistValueKeys: new Set<string>(),
    checklistValueChecklistIds: new Set<string>(),
  };

  const checklistValuesTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;
  const checklistsTable = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME;

  for (const operation of pendingOperations) {
    const collection = String(operation.collection ?? '').trim();
    const itemId = String(operation.itemId ?? operation.item_id ?? '').trim();
    addPullProtectionEntity(protection, collection, itemId);

    const operationData = parsePullProtectionData(operation.data);
    if (collection === checklistValuesTable) {
      if (operation.operation === 'checklist_partial_update') {
        // A pendência parcial usa checklist_id e a identidade física ou de
        // snapshot; preserve somente as rows realmente contidas no payload.
        addChecklistPartialProtection(protection, itemId, operation.data);
      } else {
        const checklistId = operationData?.checklist_id ?? operationData?.checklistId;
        const fieldIds = [
          operationData?.campo_id ?? operationData?.campoId,
          operationData?.snapshot_field_id ?? operationData?.snapshotFieldId,
        ].map(fieldId => String(fieldId ?? '').trim()).filter(Boolean);
        if (fieldIds.length === 0) {
          addChecklistValueProtection(protection, checklistId);
        } else {
          fieldIds.forEach(fieldId => addChecklistValueProtection(protection, checklistId, fieldId));
        }
      }
    }
  }

  for (const image of pendingImages) {
    if (String(image.itemType ?? image.item_type ?? '').toUpperCase() === 'CHECKLIST') {
      const checklistId = image.item_id ?? image.itemId;
      if (!allowMissingParentMetadata) addPullProtectionEntity(
        protection,
        checklistsTable,
        checklistId
      );
      addChecklistValueProtection(protection, checklistId, image.campo_id ?? image.campoId);
      continue;
    }

    const collection = image.collection
      ?? image.table
      ?? image.tableName
      ?? APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME;
    addPullProtectionEntity(protection, collection, image.item_id ?? image.itemId ?? image.id);
  }

  for (const deletion of pendingDeletions) {
    const deletionType = String(deletion.item_type ?? deletion.itemType ?? '').toUpperCase();
    const deletionItemId = deletion.item_id ?? deletion.itemId ?? deletion.id;
    if (deletionType === 'CHECKLIST') {
      addPullProtectionEntity(protection, checklistsTable, deletionItemId);
      addChecklistValueProtection(protection, deletionItemId);
      continue;
    }

    addPullProtectionEntity(
      protection,
      deletion.collection ?? deletion.table ?? deletion.tableName ?? deletion.item_type ?? deletion.itemType,
      deletionItemId
    );
  }

  for (const conflict of conflicts) {
    if (conflict.status === 'resolved') continue;
    const collection = String(conflict.collection ?? '').trim();
    const itemId = conflict.item_id ?? conflict.itemId;
    addPullProtectionEntity(protection, collection, itemId);
    if (collection === checklistValuesTable) {
      addChecklistValueProtection(
        protection,
        conflict.checklist_id ?? conflict.checklistId ?? itemId,
        conflict.field_id ?? conflict.fieldId
      );
    }
  }

  return protection;
};

export const isProtectedFromRemotePull = (
  tableName: string,
  row: Record<string, unknown>,
  protection: LocalPullProtection
): boolean => {
  const itemId = String(row.id ?? '').trim();
  if (itemId && protection.entityKeys.has(localPullEntityKey(tableName, itemId))) {
    return true;
  }

  if (tableName !== APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME) {
    return false;
  }

  const checklistId = String(row.checklist_id ?? '').trim();
  const campoId = String(row.campo_id ?? '').trim();
  const snapshotFieldId = String(row.snapshot_field_id ?? '').trim();
  if (!checklistId) return false;
  if (protection.checklistValueChecklistIds.has(checklistId)) return true;

  return Boolean(
    (campoId && protection.checklistValueKeys.has(localChecklistValueKey(checklistId, campoId))) ||
    (snapshotFieldId && protection.checklistValueKeys.has(localChecklistValueKey(checklistId, snapshotFieldId)))
  );
};
